import { Backdrop, ContactShadows, Float, Html } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Euler, Group, MathUtils, Quaternion } from "three";
import { lerp } from "three/src/math/MathUtils";

const pulseAnimation = keyframes`
  0% { transform: scale(0.9); }
  50% { transform: scale(1.1); }
  100% { transform: scale(0.9); }
`;

const CircularSpinButton = styled.button`
    /* Base styles */
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: radial-gradient(110.15% 67.99% at 43.57% 35.71%, #fe4c66 0%, #ff0025 100%);
    color: white;
    font-weight: bold;
    border: none;
    cursor: pointer;
    transition: transform 0.2s ease;

    /* Center text */
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: #ff3333;
    }

    /* Animation when spinning */
    &.spinning {
        animation: ${pulseAnimation} 1s ease-in-out infinite;
    }
`;

interface WheelProps {
    names: string[];
}

interface WheelRef {
    spinWheel: () => void;
}

let zoom = 0;
const Wheel = forwardRef<WheelRef, WheelProps>(({ names }, ref) => {
    const wheelRef = useRef<Group>(null);
    const previousRotationRef = useRef<number>(0);
    const [spinning, setSpinning] = useState(false);
    const [winner, setWinner] = useState<string | null>(null);
    const [speed, setSpeed] = useState(0);
    const [testWinner, setTestWinner] = useState(0);

    const segmentAngle = (Math.PI * 2) / names.length;
    const startOffset = segmentAngle / 2;
    let deg = 0;

    useFrame((state, delta) => {
        const win = calculateWinner();
        zoom = lerp(zoom, 6, 1 * delta);
        state.camera.position.set(0, 0, zoom);
        deg = lerp(deg, MathUtils.degToRad(720 + win), speed * delta);
        if (spinning && wheelRef.current) {
            const currentRotation = parseFloat(wheelRef.current.rotation.z.toFixed(4));
            const previousRotation = parseFloat(previousRotationRef.current.toFixed(4));
            const quaternion = new Quaternion();
            wheelRef.current.rotation.setFromQuaternion(
                quaternion.setFromEuler(new Euler(0, 0, deg))
            );
            if (currentRotation === previousRotation) {
                setTestWinner((testWinner + 1) % names.length);
                setSpinning(false);
            }
            previousRotationRef.current = currentRotation;
        }
    });

    const fetchWinnerFromAPI = async () => {
        // const response = await fetch("/api/get-winner");
        // const data = await response.json();
        // return data.winner;
        await null;
        return "Charlie";
    };

    const spinWheel = async () => {
        setSpinning(true);
        setWinner(null);

        const initialSpeed = 1 + Math.random() * 1.5;
        setSpeed(initialSpeed);

        const targetWinner = await fetchWinnerFromAPI();
    };

    const calculateWinner = () => {
        // const index = names.indexOf("Eve");
        const index = testWinner;
        const startAngle = MathUtils.radToDeg(index * segmentAngle);
        const endAngle = MathUtils.radToDeg((index + 1) * segmentAngle);
        const randomNum = Math.floor(Math.random() * (endAngle - startAngle + 1)) + startAngle;
        const midpoint = (startAngle + endAngle) / 2;
        return midpoint;
    };

    useImperativeHandle(ref, () => ({
        spinWheel,
    }));

    return (
        <>
            <group ref={wheelRef} rotation={[0, 0, startOffset]}>
                {names.map((name, index) => {
                    const isWinner = winner === name;
                    return (
                        <group key={name} rotation={[0, 0, 0]}>
                            <mesh>
                                <circleGeometry
                                    args={[
                                        3,
                                        32,
                                        (index + 1) * -segmentAngle - MathUtils.degToRad(-90),
                                        segmentAngle,
                                    ]}
                                />
                                <meshBasicMaterial
                                    color={index % 2 === 0 ? "#FFDA94" : "#FEF8E0"}
                                />
                            </mesh>
                            <group
                                rotation={[
                                    0,
                                    0,
                                    (index + 1) * -segmentAngle - MathUtils.degToRad(-90),
                                ]}
                            >
                                <Html
                                    center
                                    transform
                                    position={[1.85, Math.sin(segmentAngle), 0]}
                                    style={{
                                        fontSize: "9px",
                                        fontWeight: 500,
                                        color: isWinner ? "red" : "black",
                                        rotate: "60deg",
                                    }}
                                >
                                    <img
                                        src="../images/wheeldiscount.png"
                                        style={{ rotate: "-90deg" }}
                                    />
                                    <div style={{ textAlign: "center" }}>
                                        <div>{name}</div>
                                        <div>20% Off</div>
                                    </div>
                                </Html>
                            </group>
                        </group>
                    );
                })}
            </group>
            <group position={[0, 0.5, 0]}>
                <mesh rotation={[0, 0, 0]}>
                    <coneGeometry args={[0.5, 1, 3]} />
                    <meshBasicMaterial color="red" />
                </mesh>
                <Html center position={[0, -0.5, 0]}>
                    <CircularSpinButton
                        className={spinning ? "spinning" : ""}
                        onClick={() => {
                            spinWheel();
                        }}
                    >
                        GO
                    </CircularSpinButton>
                </Html>
            </group>
            {/* <Text position={[0, -2.5, 0]} fontSize={0.3} color="white">
                {winner ? `Winner: ${winner}` : "Click Spin to start!"}
            </Text> */}
        </>
    );
});

export const PageSimpleWheel: React.FC = () => {
    const wheelRef = useRef<WheelRef>(null);

    return (
        <div
            style={{
                width: "100%",
                height: "400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Canvas camera={{ position: [0, 0, 5.5], fov: 65 }}>
                <ambientLight intensity={2} />
                <spotLight intensity={1000} angle={0.15} penumbra={1} position={[0, 15, 10]} />

                {/* <Float
                    speed={2}
                    rotationIntensity={0.15}
                    floatIntensity={1}
                    floatingRange={[0.11, 0.14]}
                >
                    <Circle args={[1, 32, 0, Math.PI * 2]} position={[3, -1.5, -0.5]}>
                        <meshPhysicalMaterial roughness={1} color="red" />
                    </Circle>
                </Float>

                <Float
                    speed={2}
                    rotationIntensity={0.15}
                    floatIntensity={1}
                    floatingRange={[0, 0.24]}
                >
                    <Circle args={[0.7, 32, 0, Math.PI * 2]} position={[-3, 1.5, -0.5]}>
                        <meshPhysicalMaterial roughness={1} color="yellowgreen" />
                    </Circle>
                </Float> */}

                <Float
                    speed={7}
                    rotationIntensity={0.15}
                    floatIntensity={2}
                    floatingRange={[0.11, 0.22]}
                >
                    <Wheel
                        ref={wheelRef}
                        names={["Charlie", "Alice", "Bob", "Eve", "Dave", "Frank"]}
                    />
                </Float>
                <Backdrop receiveShadow scale={[30, 20, 15]} floor={2} position={[0, -4, -5]}>
                    <meshPhysicalMaterial roughness={1} color="#ffffff" />
                </Backdrop>
                <ContactShadows
                    position={[0, -2, 0]}
                    opacity={0.25}
                    scale={10}
                    blur={10}
                    far={0.8}
                />
            </Canvas>
        </div>
    );
};

const SpinButton = styled.button`
    /* background-color: red; */
    &:hover {
        background-color: yellowgreen !important;
    }

    padding: 10px 20px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    border-radius: 10px;
    border: none;
`;
